import { useState, useEffect } from 'react';
import { ExternalLink, Newspaper, ChevronDown, ChevronUp } from 'lucide-react';
import { cultureFeedApi } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import TimeAgo from './TimeAgo';

function CultureFeedItem({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3.5 rounded-xl bg-forest/[0.03] border border-forest/10 hover:border-forest/25 transition-colors group"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-forest/70">{item.source_name}</span>
        <div className="flex items-center gap-1.5 text-ink-muted">
          {item.published_at && (
            <TimeAgo date={item.published_at} className="text-xs" />
          )}
          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      <h4 className="text-sm font-semibold text-ink leading-snug group-hover:text-forest transition-colors line-clamp-2 font-heading">
        {item.title}
      </h4>
      {item.summary && (
        <p className="text-xs text-ink-muted mt-1 line-clamp-2 leading-relaxed">
          {item.summary}
        </p>
      )}
    </a>
  );
}

function CultureFeedSkeleton() {
  return (
    <div className="p-3.5 rounded-xl bg-forest/[0.03] border border-forest/10 space-y-2">
      <Skeleton className="h-3 w-24 bg-forest/10" />
      <Skeleton className="h-4 w-full bg-forest/10" />
      <Skeleton className="h-3 w-3/4 bg-forest/10" />
    </div>
  );
}

export default function CultureFeedSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    cultureFeedApi.getItems()
      .then((data) => setItems(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!loading && items.length === 0) return null;

  const visibleCount = expanded ? items.length : 3;
  const visibleItems = items.slice(0, visibleCount);

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper className="w-4 h-4 text-forest" />
        <h3 className="text-sm font-semibold text-ink">From the Culture</h3>
      </div>

      <div className="space-y-2">
        {loading ? (
          <>
            <CultureFeedSkeleton />
            <CultureFeedSkeleton />
            <CultureFeedSkeleton />
          </>
        ) : (
          <>
            {visibleItems.map((item) => (
              <CultureFeedItem key={item.id || item.url} item={item} />
            ))}
            {items.length > 3 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-forest/70 hover:text-forest transition-colors mx-auto mt-1"
              >
                {expanded ? (
                  <>Show less <ChevronUp className="w-3 h-3" /></>
                ) : (
                  <>Show more ({items.length - 3} more) <ChevronDown className="w-3 h-3" /></>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
